/**
 * Withdrawing the agreement to read a whole book, from the field-map page where it was given.
 *
 * The advisor is asked to confirm first, and told what withdrawing does today: reading stops and the
 * prospect scores are deleted, while contacts already saved are kept until deletion is asked for.
 * That sentence is sent with the withdrawal and stored verbatim, like the wording of the consent
 * itself, so the record says what the advisor was told.
 *
 * It works on the page's own `useCrmConsent`, so a failed save shows in the dialog and the page
 * switches back to asking for consent the moment the withdrawal is recorded.
 */
import { useState } from 'react';
import type { useCrmConsent } from '@client/hooks/useCrmConsent';

export function bookReadWithdrawalText(crmName: string): string {
  return (
    `Waterlily will stop reading your ${crmName} contacts and delete the prospect scores it worked ` +
    `out from them. Your Prospects page will not be available until you agree again. Contacts ` +
    `already saved are kept, and you can ask for any person's saved data to be deleted at any time.`
  );
}

export function useBookReadConsentWithdrawal({
  crmName,
  consent,
}: {
  crmName: string;
  consent: ReturnType<typeof useCrmConsent>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const text = bookReadWithdrawalText(crmName);

  const ask = () => {
    // A failed save from an earlier attempt should not greet the advisor when they try again.
    consent.clearError();
    setIsOpen(true);
  };

  const confirm = async () => {
    try {
      await consent.withdraw('bookRead', text);
    } catch {
      // The dialog stays open and shows `error`; the consent stands until a withdrawal is recorded.
      return;
    }
    setIsOpen(false);
  };

  return {
    isOpen,
    text,
    ask,
    confirm,
    cancel: () => setIsOpen(false),
    isSaving: consent.isSaving,
    error: consent.error,
  };
}
